import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const TimecardSummary = ({ timecard }) => {
  const [hourlyRate, setHourlyRate] = useState(null);
  const [isLoadingRate, setIsLoadingRate] = useState(true);
  const [showEmployeeInfo, setShowEmployeeInfo] = useState(false);
  const [showBidPeriod, setShowBidPeriod] = useState(false);
  
  const parsed = timecard?.parsed_json || {};
  const identity = parsed.identity || {};
  const schedule = parsed.schedule || {};
  const paySummary = parsed.pay_summary || {};
  const creditTotals = paySummary.credit_totals || {};
  const guaranteeInfo = parsed.guarantee_info || {};
  const bankInfo = parsed.bank_info || {};
  const bankSnapshot = bankInfo.bank_snapshot || {};
  const rotations = parsed.rotations || [];
  
  // Parse additional pay from dictionary format
  const additionalPay = paySummary.additional_pay || {};
  
  // Helper to find pay category value (handles both dict keys and variations)
  const getPayAmount = (key) => {
    // Try direct key lookup first
    if (additionalPay[key]) return additionalPay[key];
    
    // Try common variations
    const variations = {
      'g_slip_pay': ['G/SLIP PAY', 'GSLIP PAY', 'G/S PAY'],
      'assign_pay': ['ASSIGN PAY'],
      'reroute_pay': ['REROUTE PAY'],
      's_slip_pay': ['S/SLIP PAY', 'SSLIP PAY', 'SS PAY'],
      'pbs_pr_pay': ['PBS/PR PAY'],
      'training_pay': ['DISTRIBUTED TRNG PAY', 'TRAINING PAY']
    };
    
    if (variations[key]) {
      for (const variant of variations[key]) {
        if (additionalPay[variant]) return additionalPay[variant];
      }
    }
    
    return null;
  };

  // Fetch user's real hourly rate from Supabase user_settings
  useEffect(() => {
    const fetchHourlyRate = async () => {
      console.log('🔍 TimecardSummary: Checking for employee_id:', identity.employee_id);
      
      if (!identity.employee_id) {
        console.warn('⚠️ No employee_id found in timecard identity');
        setIsLoadingRate(false);
        return;
      }

      try {
        // Step 1: Get UUID from employee_id via user_profiles
        // Handle leading zero variants (database may have "141986" or "0141986")
        console.log('📡 Looking up UUID for employee:', identity.employee_id);
        
        const employeeIdVariants = [
          identity.employee_id,                          // As-is (e.g., "0141986")
          identity.employee_id.replace(/^0+/, ''),      // Strip leading zeros (e.g., "141986")
        ];
        
        console.log('🔍 Trying employee ID variants:', employeeIdVariants);
        
        let profile = null;
        for (const variant of employeeIdVariants) {
          const { data, error } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('employee_id', variant)
            .maybeSingle();
          
          if (data) {
            profile = data;
            console.log(`✅ Found profile with employee_id: ${variant}`);
            break;
          }
        }

        if (!profile) {
          console.warn('⚠️ No user profile found for any employee_id variant:', employeeIdVariants);
          setIsLoadingRate(false);
          return;
        }

        console.log('✅ Found UUID:', profile.id);

        // Step 2: Get user_settings with that UUID
        const { data: settings, error: settingsError } = await supabase
          .from('user_settings')
          .select('*')
          .eq('user_id', profile.id)
          .maybeSingle();

        console.log('Settings query result:', { settings, settingsError });

        if (settingsError) {
          console.error('❌ Settings query error:', settingsError);
          setIsLoadingRate(false);
          return;
        }

        if (!settings) {
          console.warn('⚠️ No user settings found for user_id:', profile.id);
          setIsLoadingRate(false);
          return;
        }

        console.log('✅ Got settings:', settings);

        // Step 3: Calculate pay rate from position + hire date + aircraft + TIMECARD YEAR
        if (settings.position && settings.date_of_hire && settings.default_aircraft_model) {
          // Get the timecard bid period year (not current year!)
          let timecardYear = new Date().getFullYear(); // Default to current year
          if (schedule.bid_period_start) {
            timecardYear = new Date(schedule.bid_period_start).getFullYear();
          }
          
          console.log(`📅 Timecard year: ${timecardYear}`);
          
          // Calculate years of service AS OF THE TIMECARD DATE
          const hire = new Date(settings.date_of_hire);
          const timecardDate = schedule.bid_period_start ? new Date(schedule.bid_period_start) : new Date();
          const yearsOfService = Math.floor((timecardDate - hire) / (365.25 * 24 * 60 * 60 * 1000)) + 1;
          
          // Map position to pay_scales format
          const positionMap = { 
            'CA': 'Captain', 
            'Captain': 'Captain', 
            'A': 'Captain',
            'FO': 'First Officer', 
            'First Officer': 'First Officer',
            'B': 'First Officer'
          };
          const payPosition = positionMap[settings.position] || settings.position;
          
          // Map aircraft codes to full names in pay_scales
          const aircraftMap = {
            '73N': 'B-737-900',
            '737-900': 'B-737-900',
            'B737-900': 'B-737-900',
            '76I': 'B-767-400',
            '767-400': 'B-767-400',
            'B767-400': 'B-767-400',
            '777': 'B-777',
            'B777': 'B-777',
            '787': 'B-787',
            'B787': 'B-787'
          };
          const mappedAircraft = aircraftMap[settings.default_aircraft_model] || settings.default_aircraft_model;
          
          console.log('🔍 Looking up pay rate:', { payPosition, mappedAircraft, yearsOfService, timecardYear });
          
          const { data: payScales, error: payError } = await supabase
            .from('pay_scales')
            .select('*')
            .eq('position', payPosition)
            .eq('aircraft_model', mappedAircraft)
            .eq('pay_year', Math.min(yearsOfService, 12))  // Cap at year 12
            .lte('effective_date', `${timecardYear}-12-31`)  // Rate must be effective by end of timecard year
            .order('effective_date', { ascending: false })
            .limit(1);

          console.log('💰 Pay scales query result:', payScales, payError);

          if (payScales && payScales.length > 0) {
            const rate = parseFloat(payScales[0].hourly_rate);
            console.log(`✅ Using ${timecardYear} rate: $${rate}/hr (effective ${payScales[0].effective_date})`);
            setHourlyRate(rate);
          } else {
            console.warn('⚠️ No pay rate found for:', { payPosition, mappedAircraft, yearsOfService, timecardYear });
          }
        }
      } catch (error) {
        console.error('❌ Failed to load hourly rate:', error);
      } finally {
        setIsLoadingRate(false);
      }
    };

    fetchHourlyRate();
  }, [identity.employee_id]);

  // Helper to convert HH:MM to decimal hours
  const timeToHours = (timeStr) => {
    if (!timeStr || timeStr === '0:00') return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours + (minutes / 60);
  };

  // Helper to format hours as HH:MM
  const formatTime = (timeStr) => {
    if (!timeStr || timeStr === '0:00') return '—';
    return timeStr;
  };

  // Extract bank transaction values from guarantee_info
  const bankTransactions = guaranteeInfo.bank_transactions || {};
  const inBank = bankSnapshot.temp_in_bank || bankInfo.temp_in_bank || '25:37';  // Starting bank balance
  const bankUsed = bankTransactions.ttl_bank_opts_award || '5:00';  // Used this month
  const outBank = bankTransactions.out_bank || '20:37';  // Ending balance
  const alv = bankSnapshot.alv || '72:18';  // Average Line Value

  // Debug: Log bank data to console
  console.log('🏦 Bank Data Debug:', {
    inBank,
    bankUsed,
    outBank,
    rawBankInfo: bankInfo,
    rawBankTransactions: bankTransactions,
    rawGuaranteeInfo: guaranteeInfo
  });
  
  // Debug: Log all credit calculation components
  console.log('💰 Credit Calculation Debug:', {
    credit_sub: creditTotals.credit_sub,
    credit_res: creditTotals.credit_res,
    credit_other: creditTotals.credit_other,
    neg_payback: creditTotals.neg_payback,
    bank_used: creditTotals.bank || creditTotals.bank_opt_1_awd,
    credit_ttl: creditTotals.credit_ttl,
    additionalPay: additionalPay,
    additionalPayOnlyFromRotations: rotations.reduce((sum, r) => {
      if (!r.additional_pay_only || r.additional_pay_only === '0:00') return sum;
      const [h, m] = r.additional_pay_only.split(':').map(Number);
      return sum + h + (m / 60);
    }, 0)
  });

  const totalCreditHours = timeToHours(creditTotals.credit_ttl);
  const bankHours = timeToHours(inBank);  // Use starting bank value
  const guaranteeTrigger = timeToHours(paySummary.reg_gslip_trigger);

  // Calculate TOTAL PAY HOURS (credit + pay_time no credit + all additional pay)
  const payTimeNoCredit = rotations.reduce((sum, r) => {
    if ((!r.credit_hours || r.credit_hours === '0:00') && r.pay_time && r.pay_time !== '0:00') {
      const [h, m] = r.pay_time.split(':').map(Number);
      return sum + h + (m / 60);
    }
    return sum;
  }, 0);
  
  const addtlPayOnlyHours = rotations.reduce((sum, r) => {
    if (!r.additional_pay_only || r.additional_pay_only === '0:00') return sum;
    const [h, m] = r.additional_pay_only.split(':').map(Number);
    return sum + h + (m / 60);
  }, 0);
  
  const gslipHours = timeToHours(getPayAmount('G/SLIP PAY') || '0:00');
  const rerouteHours = timeToHours(getPayAmount('REROUTE PAY') || '0:00');
  const sslipHours = timeToHours(getPayAmount('S/SLIP PAY') || '0:00');
  const pbsPrHours = timeToHours(getPayAmount('PBS/PR PAY') || '0:00');
  const trainingHours = timeToHours(getPayAmount('DISTRIBUTED TRNG PAY') || '0:00');
  
  const totalPayHours = totalCreditHours + payTimeNoCredit + addtlPayOnlyHours + gslipHours + rerouteHours + sslipHours + pbsPrHours + trainingHours;

  // Use real user hourly rate (from DB) or fallback to placeholder
  const effectiveRate = hourlyRate || 250.00;
  const estimatedPay = totalPayHours * effectiveRate;  // Changed from totalCreditHours to totalPayHours
  const usingPlaceholder = !hourlyRate;

  return (
    <div className="space-y-6">
      {/* Financial Summary Hero - Total Pay */}
      <div className="card p-6 bg-gradient-to-br from-accent-cyan/10 via-accent-purple/5 to-accent-green/10 border-accent-cyan/30">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h2 className="text-sm font-medium text-muted-foreground uppercase mb-2">
              {isLoadingRate ? 'Loading Pay...' : 'Total Pay Hours'}
            </h2>
            <p className="text-5xl font-bold text-accent-green">
              {Math.floor(totalPayHours)}:{String(Math.round((totalPayHours - Math.floor(totalPayHours)) * 60)).padStart(2, '0')}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Credit + Pay Time + All Additional Pay
            </p>
          </div>
          <div className="flex-1 text-right">
            <h2 className="text-sm font-medium text-muted-foreground uppercase mb-2">
              {isLoadingRate ? 'Loading...' : 'Estimated Total Pay'}
            </h2>
            <p className="text-5xl font-bold text-accent-cyan">
              ${estimatedPay.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {totalPayHours.toFixed(2)} hours × ${effectiveRate.toFixed(2)}/hr
            </p>
            <p className="text-xs text-muted-foreground/60 mt-1">
              {isLoadingRate ? 'Fetching your rate...' : 
               usingPlaceholder ? 'Using placeholder rate (update settings for accurate pay)' :
               'Based on your pay settings and current year of service'}
            </p>
          </div>
        </div>
      </div>

      {/* Top Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4 bg-gradient-to-br from-accent-cyan/10 to-accent-cyan/5 border-accent-cyan/30">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Clock" className="w-5 h-5 text-accent-cyan" />
            <h3 className="text-sm font-medium text-muted-foreground uppercase">Total Credit</h3>
          </div>
          <p className="text-3xl font-bold text-accent-cyan">{totalCreditHours.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground mt-1">{formatTime(creditTotals.credit_ttl)} hours</p>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Plane" className="w-5 h-5 text-accent-purple" />
            <h3 className="text-sm font-medium text-muted-foreground uppercase">Rotations</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{rotations.length}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {rotations.filter(r => r.block_hours && r.block_hours !== '0:00').length} with block time
          </p>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="PiggyBank" className="w-5 h-5 text-accent-green" />
            <h3 className="text-sm font-medium text-muted-foreground uppercase">Bank</h3>
          </div>
          <div className="text-sm text-foreground space-y-1">
            <div>IN BANK: {inBank}</div>
            <div>USED: {bankUsed}</div>
            <div>OUT BANK: {outBank}</div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Shield" className="w-5 h-5 text-warning" />
            <h3 className="text-sm font-medium text-muted-foreground uppercase">Monthly Guarantee</h3>
          </div>
          <div className="text-sm text-foreground space-y-1">
            <div>ALV: {alv}</div>
            <div>REG G/S TRIGGER: {formatTime(guaranteeInfo.reg_gslip_trigger || '0:00')}</div>
            <div>CREDIT APPLICABLE: {formatTime(guaranteeInfo.credit_applicable_to_reg_gslip_pay || '0:00')}</div>
          </div>
        </div>
      </div>

      {/* Employee & Schedule Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card p-6">
          <h3 
            className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2 cursor-pointer hover:text-accent-cyan transition-colors"
            onClick={() => setShowEmployeeInfo(!showEmployeeInfo)}
          >
            <Icon name="User" className="w-5 h-5 text-accent-cyan" />
            Employee Information
            <Icon name={showEmployeeInfo ? "ChevronUp" : "ChevronDown"} className="w-4 h-4 ml-auto" />
          </h3>
          {showEmployeeInfo && (
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-accent-cyan/10">
                <span className="text-sm text-muted-foreground">Name</span>
                <span className="font-semibold text-foreground">{identity.employee_name || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-accent-cyan/10">
                <span className="text-sm text-muted-foreground">Employee ID</span>
                <span className="font-mono text-foreground">{identity.employee_id || identity.crew_id || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-accent-cyan/10">
                <span className="text-sm text-muted-foreground">Base</span>
                <span className="font-semibold text-accent-cyan">{identity.base || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-accent-cyan/10">
                <span className="text-sm text-muted-foreground">Equipment</span>
                <span className="font-semibold text-accent-purple">{identity.equipment || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-muted-foreground">Position</span>
                <span className="font-semibold text-foreground">{identity.position === 'A' ? 'Captain' : identity.position === 'B' ? 'First Officer' : identity.position || 'N/A'}</span>
              </div>
            </div>
          )}
        </div>

        <div className="card p-6">
          <h3 
            className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2 cursor-pointer hover:text-accent-purple transition-colors"
            onClick={() => setShowBidPeriod(!showBidPeriod)}
          >
            <Icon name="Calendar" className="w-5 h-5 text-accent-purple" />
            Bid Period Details
            <Icon name={showBidPeriod ? "ChevronUp" : "ChevronDown"} className="w-4 h-4 ml-auto" />
          </h3>
          {showBidPeriod && (
            <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-accent-cyan/10">
              <span className="text-sm text-muted-foreground">Period</span>
              <span className="font-semibold text-foreground">{schedule.bid_period || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-accent-cyan/10">
              <span className="text-sm text-muted-foreground">Start Date</span>
              <span className="font-mono text-foreground">{schedule.bid_period_start || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-accent-cyan/10">
              <span className="text-sm text-muted-foreground">End Date</span>
              <span className="font-mono text-foreground">{schedule.bid_period_end || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-accent-cyan/10">
              <span className="text-sm text-muted-foreground">Month</span>
              <span className="font-semibold text-foreground">
                {schedule.bid_period_normalized?.month_name || 'N/A'} {schedule.bid_period_normalized?.year || ''}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-muted-foreground">Uploaded</span>
              <span className="text-sm text-muted-foreground">
                {timecard.created_at ? new Date(timecard.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Credit Calculation Flow - The Homework */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
          <Icon name="Calculator" className="w-5 h-5 text-accent-cyan" />
          Credit Calculation Breakdown
        </h3>
        
        {/* CREDIT FLOW */}
        <div className="mb-8">
          <h4 className="text-sm font-semibold text-accent-cyan uppercase mb-4 flex items-center gap-2">
            <Icon name="TrendingUp" className="w-4 h-4" />
            Total Credit Hours (The Math)
          </h4>
          
          <div className="space-y-2 bg-accent-cyan/5 p-4 rounded-lg border border-accent-cyan/20">
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-foreground">Base Credit (from rotations)</span>
              <span className="font-mono text-lg text-foreground font-semibold">{formatTime(creditTotals.credit_sub)}</span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-t border-accent-cyan/10">
              <span className="text-sm text-muted-foreground pl-4">+ Res Guar</span>
              <span className="font-mono text-muted-foreground">{formatTime(creditTotals.credit_res)}</span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-muted-foreground pl-4">+ Other Guar</span>
              <span className="font-mono text-muted-foreground">{formatTime(creditTotals.credit_other)}</span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-muted-foreground pl-4">- Negative Payback</span>
              <span className="font-mono text-error">{formatTime(creditTotals.neg_payback)}</span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-t border-accent-cyan/20">
              <span className="text-sm text-accent-green pl-4">+ Bank Used This Month</span>
              <span className="font-mono text-accent-green font-semibold">{formatTime(creditTotals.bank || creditTotals.bank_opt_1_awd)}</span>
            </div>
            
            <div className="flex justify-between items-center py-3 mt-2 border-t-2 border-accent-cyan/40 bg-accent-cyan/10 -mx-4 px-4">
              <span className="text-base font-bold text-foreground">= TOTAL CREDIT HOURS</span>
              <span className="font-mono text-2xl text-accent-cyan font-bold">{formatTime(creditTotals.credit_ttl)}</span>
            </div>
          </div>
        </div>

        {/* ADDITIONAL PAY (NO CREDIT) */}
        <div>
          <h4 className="text-sm font-semibold text-warning uppercase mb-4 flex items-center gap-2">
            <Icon name="DollarSign" className="w-4 h-4" />
            Additional Pay Hours (No Credit)
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 bg-warning/5 p-4 rounded-lg border border-warning/20">
              <div className="flex justify-between items-center py-2 border-b border-warning/10">
                <span className="text-sm text-foreground">G/SLIP PAY</span>
                <span className="font-mono text-warning font-semibold">
                  {formatTime(getPayAmount('g_slip_pay') || getPayAmount('G/SLIP PAY') || '0:00')}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-warning/10">
                <span className="text-sm text-foreground">ASSIGN PAY</span>
                <span className="font-mono text-warning font-semibold">
                  {formatTime(getPayAmount('assign_pay') || getPayAmount('ASSIGN PAY') || '0:00')}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-warning/10">
                <span className="text-sm text-foreground">REROUTE PAY</span>
                <span className="font-mono text-warning font-semibold">
                  {formatTime(getPayAmount('reroute_pay') || getPayAmount('REROUTE PAY') || '0:00')}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-foreground">S/SLIP PAY</span>
                <span className="font-mono text-warning font-semibold">
                  {formatTime(getPayAmount('s_slip_pay') || getPayAmount('S/SLIP PAY') || '0:00')}
                </span>
              </div>
            </div>

            <div className="space-y-2 bg-accent-purple/5 p-4 rounded-lg border border-accent-purple/20">
              <div className="flex justify-between items-center py-2 border-b border-accent-purple/10">
                <span className="text-sm text-foreground">PBS/PR PAY</span>
                <span className="font-mono text-accent-purple font-semibold">
                  {formatTime(getPayAmount('pbs_pr_pay') || getPayAmount('PBS/PR PAY') || '0:00')}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-accent-purple/10">
                <span className="text-sm text-foreground">TRAINING PAY</span>
                <span className="font-mono text-accent-purple font-semibold">
                  {formatTime(getPayAmount('training_pay') || getPayAmount('DISTRIBUTED TRNG PAY') || '0:00')}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-foreground">ADDTL PAY ONLY</span>
                <span className="font-mono text-accent-purple font-semibold">
                  {(() => {
                    // Sum ADDTL PAY ONLY from rotations
                    const total = rotations.reduce((sum, r) => {
                      if (!r.additional_pay_only || r.additional_pay_only === '0:00') return sum;
                      const [h, m] = r.additional_pay_only.split(':').map(Number);
                      return sum + h + (m / 60);
                    }, 0);
                    const hours = Math.floor(total);
                    const minutes = Math.round((total - hours) * 60);
                    return total > 0 ? `${hours}:${minutes.toString().padStart(2, '0')}` : '—';
                  })()}
                </span>
              </div>
            </div>
          </div>

          {/* Note about pay vs credit */}
          <div className="mt-4 p-3 bg-muted/30 rounded-lg border border-muted">
            <p className="text-xs text-muted-foreground">
              <Icon name="Info" className="w-3 h-3 inline mr-1" />
              <strong>Note:</strong> Additional pay hours are paid at your hourly rate but do not count toward your credit hours total.
            </p>
          </div>
        </div>

        {/* TOTAL PAY HOURS - THE BIG NUMBER */}
        <div className="mt-8">
          <h4 className="text-sm font-semibold text-accent-green uppercase mb-4 flex items-center gap-2">
            <Icon name="TrendingUp" className="w-4 h-4" />
            Total Pay Hours (What You Get Paid For)
          </h4>
          
          <div className="space-y-2 bg-gradient-to-br from-accent-green/10 to-accent-cyan/10 p-6 rounded-lg border-2 border-accent-green/40">
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-foreground">Credit Hours (with credit)</span>
              <span className="font-mono text-lg text-accent-cyan font-semibold">{formatTime(creditTotals.credit_ttl)}</span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-t border-accent-green/20">
              <span className="text-sm text-foreground pl-4">+ Pay Time (rotations w/o credit)</span>
              <span className="font-mono text-warning">
                {(() => {
                  // Sum pay_time from rotations without credit
                  const total = rotations.reduce((sum, r) => {
                    // If no credit_hours or credit is 0:00, use pay_time instead
                    if ((!r.credit_hours || r.credit_hours === '0:00') && r.pay_time && r.pay_time !== '0:00') {
                      const [h, m] = r.pay_time.split(':').map(Number);
                      return sum + h + (m / 60);
                    }
                    return sum;
                  }, 0);
                  const hours = Math.floor(total);
                  const minutes = Math.round((total - hours) * 60);
                  return total > 0 ? `${hours}:${minutes.toString().padStart(2, '0')}` : '0:00';
                })()}
              </span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-foreground pl-4">+ ADDTL PAY ONLY (rotations)</span>
              <span className="font-mono text-accent-purple">
                {(() => {
                  const total = rotations.reduce((sum, r) => {
                    if (!r.additional_pay_only || r.additional_pay_only === '0:00') return sum;
                    const [h, m] = r.additional_pay_only.split(':').map(Number);
                    return sum + h + (m / 60);
                  }, 0);
                  const hours = Math.floor(total);
                  const minutes = Math.round((total - hours) * 60);
                  return total > 0 ? `${hours}:${minutes.toString().padStart(2, '0')}` : '0:00';
                })()}
              </span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-foreground pl-4">+ G/SLIP PAY</span>
              <span className="font-mono text-warning">{formatTime(getPayAmount('G/SLIP PAY') || '0:00')}</span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-foreground pl-4">+ REROUTE PAY</span>
              <span className="font-mono text-warning">{formatTime(getPayAmount('REROUTE PAY') || '0:00')}</span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-foreground pl-4">+ S/SLIP PAY</span>
              <span className="font-mono text-warning">{formatTime(getPayAmount('S/SLIP PAY') || '0:00')}</span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-foreground pl-4">+ PBS/PR PAY</span>
              <span className="font-mono text-accent-purple">{formatTime(getPayAmount('PBS/PR PAY') || '0:00')}</span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-foreground pl-4">+ TRAINING PAY</span>
              <span className="font-mono text-accent-purple">{formatTime(getPayAmount('DISTRIBUTED TRNG PAY') || '0:00')}</span>
            </div>
            
            <div className="flex justify-between items-center py-4 mt-3 border-t-2 border-accent-green/60 bg-gradient-to-r from-accent-green/20 to-accent-cyan/20 -mx-6 px-6">
              <span className="text-lg font-bold text-foreground uppercase">= TOTAL PAY HOURS</span>
              <span className="font-mono text-3xl text-accent-green font-bold">
                {(() => {
                  // Calculate total: credit + pay_time (no credit) + all additional pay
                  const creditHours = timeToHours(creditTotals.credit_ttl);
                  
                  // Pay time from rotations without credit
                  const payTimeNoCredit = rotations.reduce((sum, r) => {
                    if ((!r.credit_hours || r.credit_hours === '0:00') && r.pay_time && r.pay_time !== '0:00') {
                      const [h, m] = r.pay_time.split(':').map(Number);
                      return sum + h + (m / 60);
                    }
                    return sum;
                  }, 0);
                  
                  const addtlPayOnly = rotations.reduce((sum, r) => {
                    if (!r.additional_pay_only || r.additional_pay_only === '0:00') return sum;
                    const [h, m] = r.additional_pay_only.split(':').map(Number);
                    return sum + h + (m / 60);
                  }, 0);
                  
                  const gslipHours = timeToHours(getPayAmount('G/SLIP PAY') || '0:00');
                  const rerouteHours = timeToHours(getPayAmount('REROUTE PAY') || '0:00');
                  const sslipHours = timeToHours(getPayAmount('S/SLIP PAY') || '0:00');
                  const pbsPrHours = timeToHours(getPayAmount('PBS/PR PAY') || '0:00');
                  const trainingHours = timeToHours(getPayAmount('DISTRIBUTED TRNG PAY') || '0:00');
                  
                  const totalHours = creditHours + payTimeNoCredit + addtlPayOnly + gslipHours + rerouteHours + sslipHours + pbsPrHours + trainingHours;
                  const hours = Math.floor(totalHours);
                  const minutes = Math.round((totalHours - hours) * 60);
                  return `${hours}:${minutes.toString().padStart(2, '0')}`;
                })()}
              </span>
            </div>
            
            <div className="mt-3 p-3 bg-accent-cyan/10 rounded border border-accent-cyan/30 -mx-6 mx-0">
              <p className="text-xs text-muted-foreground text-center">
                <Icon name="Info" className="w-3 h-3 inline mr-1" />
                This is the total hours you'll be paid for this month - multiply by your hourly rate for total pay
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimecardSummary;
